import { createAsyncThunk } from "@reduxjs/toolkit";
import { httpGet } from "@/lib/http"
import { ProgressTrackerState, UserState } from "@/types";
import { serializedError } from "../helper";
import isEmpty from "lodash/isEmpty";

const getTotalUsersData = async ({ dispatch, users }: { dispatch: any, users: any }) => {
  const res = await httpGet({ url: 'admin/users?byInterests=true', dispatch });
  const { interestsUserCount, userGrowth } = res?.data || {};

  const breakdown = interestsUserCount
    .map(({ interestName, totalCount }: { interestName: string, totalCount: number }) =>
      ({ type: interestName, count: totalCount }));

  const growthComparison = {} as any;
  userGrowth.map(({ type: _type, currentCount, previousCount, percentage }: {
    type: string, currentCount: number, previousCount: number, percentage: number
  }) => {
    let type = _type;
    if (_type === 'financial year') type = 'financial'
    if (_type === '6 months') type = 'sixMonths';

    growthComparison[type] = {
      previous: previousCount,
      current: currentCount,
      percentage
    }
  });

  return {
    total: users.length || (growthComparison['financial']?.current || 0) + (growthComparison['financial']?.previous || 0), // as of now
    daily: growthComparison['daily']?.current || 0,
    weekly: growthComparison['weekly']?.current || 0,
    monthly: growthComparison['monthly']?.current || 0,
    quarterly: growthComparison['quarterly']?.current || 0,
    yearly: growthComparison['financial']?.current || 0,
    growthData: Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      users: Math.floor(Math.random() * 1000) + 500,
    })),
    growthComparison,
    breakdown
  };
}

const getActiveUsersData = async () => {
  const res = await fetch("/api/analytics", { credentials: "include" })
  const analytics = await res.json() || {}

  return {
    total: analytics?.total?.today?.activeUsers || 0,
    daily: analytics?.daily?.today?.activeUsers || 0,
    weekly: analytics?.weekly?.today?.activeUsers || 0,
    monthly: analytics?.monthly?.today?.activeUsers || 0,
    quarterly: analytics?.quarterly?.today?.activeUsers || 0,
    yearly: analytics?.yearly?.today?.activeUsers || 0,
    analytics,
    sessionData: Array.from({ length: 21 }, (_, i) => ({
      time: i,
      sessions: Math.floor(Math.random() * 100) + 20,
    })),
    engagement: {
      passive: 408,
      active: 408,
    },
    currentlyTaking: 214,
  }
}

const getQuestionsAttemptedData = async ({ dispatch }: { dispatch: any }) => {
  const res = await httpGet({ url: 'admin/users?byQuestionsAttempted=true', dispatch });
  const { questionsAttemptedByUsers } = res?.data || {};

  let leaderboard = {
    daily: {} as any,
    weekly: {} as any,
    monthly: {} as any,
    quarterly: {} as any,
    yearly: {} as any,
    total: {} as any
  };
  let subjects = {
    daily: {} as any,
    weekly: {} as any,
    monthly: {} as any,
    quarterly: {} as any,
    yearly: {} as any,
    total: {} as any,
  };
  let userSubjects = {} as any;
  const periods = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'total'] as const;

  (questionsAttemptedByUsers || []).forEach(({ userEmail, questionsAttempted }: { userEmail: string, questionsAttempted: any }) => {
    periods.forEach(period => {
      // leaderboard
      leaderboard[period][userEmail] = parseFloat((
        ((questionsAttempted[period]?.correctAnswersSelected || 0) / (questionsAttempted[period]?.totalQuestionsAttempted || 1)) * 100
      ).toFixed(2));

      // all subjects attempted count
      (questionsAttempted[period]?.subjectQuestionsAttempedCount || []).map((item: any) => {
        subjects[period][item.subjectName] = (subjects[period]?.[item.subjectName] || 0) + item.questionsAttempted; // to be changed to correctAnswer

        if (!userSubjects[period]) {
          userSubjects[period] = {}
        }

        if (!userSubjects[period][userEmail]) {
          userSubjects[period][userEmail] = {}
        }

        userSubjects[period][userEmail][item.subjectName] = {
          attempted: item.questionsAttempted || 0,
          correct: item.correctAnswersSelected || 0,
          completionRate: (item.correctAnswersSelected || 0) / (item.questionsAttempted || 1) * 100,
        };
      });
    });

    // calculate user performance by subject attempt count
    const totalSubjectAttemptedCount = questionsAttempted.total?.subjectQuestionsAttempedCount || [];
    totalSubjectAttemptedCount.forEach((item: any) => {
      if (!userSubjects[userEmail]) {
        userSubjects[userEmail] = {};
      }

      userSubjects[userEmail][item.subjectName] = {
        attempted: item.questionsAttempted || 0,
        correct: item.correctAnswersSelected || 0,
        completionRate: (item.correctAnswersSelected || 0) / (item.questionsAttempted || 1) * 100,
      };
    });
  });
  // heatmapData: Array.from({ length: 24 }, (_, i) => ({
  //   hour: i,
  //   attempts: Math.floor(Math.random() * 100) + 20,
  // })),
  // pieData: [
  //   { name: "Correct", value: 62, color: "#10b981" },
  //   { name: "Incorrect", value: 38, color: "#f97316" },
  // ],
  // subjectsData: [
  //   { subject: "Navigation", attempts: 3200 },
  //   { subject: "Meteorology", attempts: 2800 },
  //   { subject: "Technical General", attempts: 2400 },
  //   { subject: "Regulations", attempts: 1800 },
  //   { subject: "Radio", attempts: 1200 },
  // ],

  return {
    leaderboard,
    subjects,
    userSubjects,
  };
}

const getTotalAttemptedQuestions = async ({ dispatch }: { dispatch: any }) => {
  const res = await httpGet({ url: 'admin/questionsattempted', dispatch });
  const { questionsAttempted } = res?.data || {};

  const allQuestionsAttempted = {} as any
  const allQuestionsShown = {} as any
  const allHourlyQuestionsAttempted = {} as any
  const allCorrectAnswersSelected = {} as any
  const allIncorrectAnswersSelected = {} as any
  const avgPerformance = {} as any
  const completionRate = {} as any

  const allSubjects = {} as any

  const periods = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'total'] as const;
  periods.forEach(period => {
    allQuestionsAttempted[period] = questionsAttempted[period]?.totalQuestionsAttempted || 0;
    allQuestionsShown[period] = questionsAttempted[period]?.totalQuestionsShown || 0;
    allHourlyQuestionsAttempted[period] = questionsAttempted[period]?.hourly || [];
    allCorrectAnswersSelected[period] = questionsAttempted[period]?.correctAnswersSelected || 0;
    allIncorrectAnswersSelected[period] = questionsAttempted[period]?.incorrectAnswersSelected || 0;

    (questionsAttempted[period]?.subjectQuestionsAttempedCount || []).map((item: any) => {
      if (!allSubjects?.[period]) {
        allSubjects[period] = {};
      }

      allSubjects[period][item.subjectName] = {
        attempted: item.questionsAttempted || 0,
        correct: item.correctAnswersSelected || 0,
        incorrect: item.incorrectAnswersSelected || 0,
        shown: item.questionsShown || 0,
        percentageCorrect: (item.correctAnswersSelected || 0) / (item.questionsShown || 1) * 100,
        completionRate: (item.questionsAttempted || 0) / (item.questionsShown || 1) * 100,
      };
    });

    avgPerformance[period] = (allCorrectAnswersSelected[period] || 0) / (allQuestionsAttempted[period] || 1) * 100
    completionRate[period] = (allQuestionsAttempted[period] || 0) / (allQuestionsShown[period] || 1) * 100
  });

  return {
    allQuestionsAttempted,
    allQuestionsShown,
    allHourlyQuestionsAttempted,
    allCorrectAnswersSelected,
    allIncorrectAnswersSelected,
    allSubjects,
    avgPerformance,
    completionRate
  }
}

const getQuestionsUploaded = async ({ dispatch }: { dispatch: any }) => {
  const res = await httpGet({ url: 'admin/questionbank', dispatch });
  const { questionBank } = res?.data || {};

  const toData = (key: string, value: number | string) => ({
    id: key,
    value
  })

  let yearlyUploaded = {} as any;
  const currentYear = new Date().getFullYear();
  const years = [];

  for (let i = 0; i < 5; i++) {
    yearlyUploaded[currentYear - i] = {
      daily: [],
      weekly: [],
      monthly: [],
      quarterly: [],
      yearly: [],
    } as any;
    years.push(currentYear - i);
  }

  years.forEach(year => {
    const daily = questionBank?.daily?.filter((item: any) => item.period.endsWith(year)) || [];
    const weekly = questionBank?.weekly?.filter((item: any) => item.period.endsWith(year)) || [];
    const monthly = questionBank?.monthly?.filter((item: any) => item.period.endsWith(year)) || [];
    const quarterly = questionBank?.quarterly?.filter((item: any) => item.period.endsWith(year)) || [];
    const yearly = questionBank?.yearly?.filter((item: any) => item.period.endsWith(year)) || [];

    yearlyUploaded[year].daily = daily.map((item: any) => toData(item.period, item.questionsCount));
    yearlyUploaded[year].weekly = weekly.map((item: any) => toData(item.period.split(',')[0], item.questionsCount));
    yearlyUploaded[year].monthly = monthly.map((item: any) => toData(item.period.split(' ')[0], item.questionsCount));
    yearlyUploaded[year].quarterly = quarterly.map((item: any) => toData(item.period.split(' ')[0], item.questionsCount));
    yearlyUploaded[year].yearly = yearly.map((item: any) => toData(item.period, item.questionsCount));
  })

  const contributedBy = (questionBank?.contributedBy || []).map((item: any) => toData(item.name, item.questionsCount));

  return {
    totalUploaded: questionBank?.totalCount || 0,
    yearlyUploaded,
    contributedBy,
  }
}

export const initProgressTracker = createAsyncThunk(
  "progressTracker/init",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as { progressTracker: ProgressTrackerState, user: UserState };
      const users = state.user.users;
      let progressTracker = state.progressTracker || {};
      let { totalUsersData, activeUsersData, questionsAttemptedData, totalQuestionsAttemptedData, questionsUploaded } = progressTracker;

      // call apis if not already in the state

      if (isEmpty(progressTracker) || isEmpty(progressTracker?.totalUsersData)) {
        totalUsersData = await getTotalUsersData({ dispatch, users });
      }

      if (isEmpty(progressTracker) || isEmpty(progressTracker?.activeUsersData)) {
        activeUsersData = await getActiveUsersData();
      }

      if (isEmpty(progressTracker) || isEmpty(progressTracker?.questionsAttemptedData)) {
        questionsAttemptedData = await getQuestionsAttemptedData({ dispatch });
      }

      if (isEmpty(progressTracker) || isEmpty(progressTracker?.totalQuestionsAttemptedData)) {
        totalQuestionsAttemptedData = await getTotalAttemptedQuestions({ dispatch });
      }

      if (isEmpty(progressTracker) || isEmpty(progressTracker?.questionsUploaded)) {
        questionsUploaded = await getQuestionsUploaded({ dispatch });
      }

      return {
        ...progressTracker,
        totalUsersData,
        activeUsersData,
        questionsAttemptedData,
        totalQuestionsAttemptedData,
        questionsUploaded,
      }
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);
